import { useCallback, useEffect, useRef, useState } from 'react'
import { ChevronLeft, ChevronRight, MessageSquareText } from 'lucide-react'
import SocialTikTokIcon from '~/src/components/SocialTikTokIcon'
import SocialInstagramIcon from '~/src/components/SocialInstagramIcon'

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

type Platform = 'tiktok' | 'instagram' | 'reddit' | 'youtube' | 'community'

export interface SeoReportProductQuotesProps {
  quotes: Array<{
    text: string
    source?: string
    url?: string | null
    /**
     * Structured platform from the upstream `social_signals` LLM output.
     * Prefer this over `detectPlatform` so we don't keyword-match a
     * free-text source label. Optional — legacy quote sources
     * (socialEvidence.quotes, whyCommunity, product_quotes) don't carry it
     * and we fall back to the old detection.
     */
    platform?: Platform
  }>
}

// ---------------------------------------------------------------------------
// Platform icon helper
// ---------------------------------------------------------------------------

function detectPlatform(source?: string, url?: string | null): Platform {
  const lower = source?.toLowerCase() ?? ''
  if (lower.includes('tiktok')) return 'tiktok'
  if (lower.includes('instagram') || lower.includes('reel')) return 'instagram'
  if (lower.includes('reddit')) return 'reddit'
  if (lower.includes('youtube') || lower.includes('video')) return 'youtube'

  if (url) {
    if (url.includes('tiktok.com')) return 'tiktok'
    if (url.includes('instagram.com')) return 'instagram'
    if (url.includes('reddit.com')) return 'reddit'
    if (url.includes('youtube.com') || url.includes('youtu.be'))
      return 'youtube'
  }

  return 'community'
}

function PlatformIcon({ platform }: { platform: Platform }) {
  if (platform === 'tiktok') {
    return <SocialTikTokIcon className='size-4' />
  }
  if (platform === 'instagram') {
    return <SocialInstagramIcon className='size-4' />
  }
  if (platform === 'reddit') {
    return (
      <img
        src='/assets/social/platforms/reddit.svg'
        alt='Reddit'
        loading='lazy'
        className='size-4 object-contain'
      />
    )
  }
  if (platform === 'youtube') {
    return (
      <img
        src='/assets/social/platforms/youtube.svg'
        alt='YouTube'
        loading='lazy'
        className='size-4 object-contain'
      />
    )
  }
  return <MessageSquareText className='size-4 text-foreground-muted' />
}

// Resolves a favicon for the source's origin via Google's faviconV2 service.
// Failover: if the URL is invalid we return null and the caller falls back to
// `<PlatformIcon />` (Reddit / YouTube / Instagram / TikTok / community
// glyph). At render time, an `onError` handler on the favicon <img> flips
// `faviconError` true and we swap to the platform icon there too — so any
// network failure from the favicon CDN also degrades to the platform glyph.
function getFaviconUrl(url: string | null | undefined): string | null {
  if (!url) return null

  try {
    const origin = new URL(url).origin
    return `https://t0.gstatic.com/faviconV2?client=SOCIAL&type=FAVICON&fallback_opts=TYPE,SIZE,URL&url=${encodeURIComponent(origin)}&size=64`
  } catch {
    return null
  }
}

const PLATFORM_LABELS: Record<Platform, string> = {
  tiktok: 'TikTok',
  instagram: 'Instagram',
  reddit: 'Reddit',
  youtube: 'YouTube',
  community: 'Community',
}

// ---------------------------------------------------------------------------
// Sub-components
// ---------------------------------------------------------------------------

function QuoteCard({
  text,
  source,
  url,
  platform: providedPlatform,
}: {
  text: string
  source?: string
  url?: string | null
  platform?: Platform
}) {
  const platform = providedPlatform ?? detectPlatform(source, url)
  const displaySource =
    source === 'Community' ? PLATFORM_LABELS[platform] : source
  const faviconUrl = getFaviconUrl(url)
  const [faviconError, setFaviconError] = useState(false)

  return (
    <blockquote
      className='flex w-[300px] flex-shrink-0 snap-start flex-col gap-4 rounded-2xl bg-accent p-4'
      {...(url ? { cite: url } : {})}
    >
      <p className='text-pretty text-sm italic leading-5 text-foreground'>
        &ldquo;{text}&rdquo;
      </p>
      {source || url ? (
        <div className='mt-auto flex items-center gap-1.5'>
          {faviconUrl && !faviconError ? (
            <span className='flex size-4 shrink-0 items-center justify-center overflow-hidden rounded-sm bg-white'>
              <img
                src={faviconUrl}
                alt=''
                loading='lazy'
                className='size-4 object-contain'
                onError={() => setFaviconError(true)}
              />
            </span>
          ) : (
            <PlatformIcon platform={platform} />
          )}
          {url && displaySource ? (
            <a
              href={url}
              target='_blank'
              rel='noopener noreferrer nofollow'
              className='text-xs font-semibold leading-[18px] text-info-700 hover:underline'
            >
              {displaySource}
            </a>
          ) : displaySource ? (
            <span className='text-xs font-semibold leading-[18px] text-info-700'>
              {displaySource}
            </span>
          ) : (
            <span className='text-xs font-semibold leading-[18px] text-info-700'>
              Source
            </span>
          )}
        </div>
      ) : null}
    </blockquote>
  )
}

// ---------------------------------------------------------------------------
// Main component
// ---------------------------------------------------------------------------

// Toggles edge-fade visibility on a horizontally scrollable container. Writes
// opacity directly to the fade <div>s through refs so we don't re-render the
// whole carousel on every scroll event — the only thing changing is the fade
// element's style, and that's cheaper as a direct DOM write.
function useScrollFadeIndicators<T extends HTMLElement>() {
  const scrollRef = useRef<T | null>(null)
  const leftFadeRef = useRef<HTMLDivElement | null>(null)
  const rightFadeRef = useRef<HTMLDivElement | null>(null)

  const update = useCallback(() => {
    const el = scrollRef.current
    if (!el) return
    const maxScroll = el.scrollWidth - el.clientWidth
    const showLeft = el.scrollLeft > 1
    const showRight = maxScroll > 1 && el.scrollLeft < maxScroll - 1
    if (leftFadeRef.current) {
      leftFadeRef.current.style.opacity = showLeft ? '1' : '0'
    }
    if (rightFadeRef.current) {
      rightFadeRef.current.style.opacity = showRight ? '1' : '0'
    }
  }, [])

  return { scrollRef, leftFadeRef, rightFadeRef, update }
}

export function SeoReportProductQuotes({
  quotes,
}: SeoReportProductQuotesProps) {
  const cardWidth = 300
  const cardGap = 12
  const { scrollRef, leftFadeRef, rightFadeRef, update } =
    useScrollFadeIndicators<HTMLDivElement>()

  const scrollBy = useCallback(
    (direction: 'left' | 'right') => {
      if (!scrollRef.current) return
      const amount =
        direction === 'left' ? -(cardWidth + cardGap) : cardWidth + cardGap
      scrollRef.current.scrollBy({ left: amount, behavior: 'smooth' })
    },
    [scrollRef]
  )

  // Sync indicators after the carousel mounts and whenever the quote list
  // changes (its scrollWidth depends on quote count).
  useEffect(() => {
    update()
  }, [update, quotes.length])

  if (quotes.length === 0) return null

  return (
    <div className='flex flex-col gap-3'>
      <div className='flex items-center justify-between'>
        <h3 className='text-xl font-semibold leading-[30px] text-foreground'>
          What people are saying
        </h3>
        {quotes.length > 1 && (
          <div className='hidden gap-2 md:flex'>
            <button
              type='button'
              onClick={() => scrollBy('left')}
              className='flex h-8 w-8 items-center justify-center rounded-full border border-border bg-white transition-colors hover:bg-gray-50'
              aria-label='Previous quote'
            >
              <ChevronLeft className='h-5 w-5 text-foreground' />
            </button>
            <button
              type='button'
              onClick={() => scrollBy('right')}
              className='flex h-8 w-8 items-center justify-center rounded-full border border-border bg-white transition-colors hover:bg-gray-50'
              aria-label='Next quote'
            >
              <ChevronRight className='h-5 w-5 text-foreground' />
            </button>
          </div>
        )}
      </div>
      <div className='relative'>
        <div
          ref={scrollRef}
          onScroll={update}
          className='flex snap-x snap-mandatory gap-3 overflow-x-auto scroll-smooth pb-2 touch-pan-x touch-pan-y scrollbar-none'
        >
          {quotes.map((quote, index) => (
            <QuoteCard
              key={`quote-${index}`}
              text={quote.text}
              source={quote.source}
              url={quote.url}
              platform={quote.platform}
            />
          ))}
        </div>
        <div
          ref={leftFadeRef}
          style={{ opacity: 0 }}
          className='pointer-events-none absolute inset-y-0 left-0 w-[60px] bg-gradient-to-r from-white to-transparent transition-opacity duration-500 ease-out'
          aria-hidden='true'
        />
        <div
          ref={rightFadeRef}
          style={{ opacity: 0 }}
          className='pointer-events-none absolute inset-y-0 right-0 w-[60px] bg-gradient-to-l from-white to-transparent transition-opacity duration-500 ease-out'
          aria-hidden='true'
        />
      </div>
    </div>
  )
}
